import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import { catchError, Observable, switchMap, throwError, timeout } from 'rxjs';
import { API_ENDPOINTS, ApiEndpoints } from './api-endpoints.token';
import { VenueTokenService } from './venue-token.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  readonly e: ApiEndpoints = inject(API_ENDPOINTS); // expose endpoints

  // This method is used to initialize the class instance using http
  constructor(
    private http: HttpClient,
    private venueTokenService: VenueTokenService
  ) { }

  // This method is used to full path using root and endpoint
  private fullPath(root: string, endpoint: string) {
    if (root && root !="wso2API") {
      return `${environment.api_gateway}${root}/${endpoint}`;
    } else {
      if(root == 'wso2API'){
         return `${environment.wso2APIURL}${endpoint}`;
      }
      return `${environment.api_gateway}${endpoint}`;
    }
  }


  // This method is used to get language
  private getLanguage() {
    let lang: string = 'ENU';
    if (localStorage.getItem("selectedLang")) {
      lang = localStorage.getItem("selectedLang") == 'ar' ? 'ARA' : 'ENU'; //ARA
    }
    return lang;
  }

  private withVenueToken<TRes>(requestFactory: () => Observable<TRes>): Observable<TRes> {
    return this.venueTokenService.ensureVenueToken().pipe(
      switchMap(() => requestFactory())
    );
  }
  /** Generic POST */
  // This method is used to post using api root, path, body, and params
  post<TReq, TRes = unknown>(
    apiRoot: string,
    path: string,
    body: any,
    params?: HttpParams | { [key: string]: string | number }
  ): Observable<TRes> {
    //Normalize language fields
    if (body?.body) {
      const lang = this.getLanguage();
      ['LanguageCode', 'Language', 'DCLanguage'].forEach((key) => {
        if (body.body[key]) body.body[key] = lang;
      });
    }

    //Determine API endpoint based on environment mode
    const mode = (environment as any).apiMode || 'gateway';
    let request$: Observable<TRes>;

    switch (mode) {
      case 'siebel':
        request$ = this.http.post<TRes>(`${environment.api_siebel}RunProcess`, body, { params });
        break;
      case 'soa':
        request$ = this.http.post<TRes>(`${environment.api_soa_dev}${apiRoot}/${path}`, body, { params });
        break;
      default:
        request$ = this.http.post<TRes>(this.fullPath(apiRoot, path), body, { params });
        break;
    }

    return request$.pipe(
      catchError((error) => {
        console.error('API request failed:', error);
        return throwError(() => error);
      })
    );
  }

  postWithoutRetry<TReq, TRes = unknown>(
    apiRoot: string,
    path: string,
    body: any,
    params?: HttpParams | { [key: string]: string | number }
  ): Observable<TRes> {
    if (body?.body) {
      const lang = this.getLanguage();
      ['LanguageCode', 'Language', 'DCLanguage'].forEach((key) => {
        if (body.body[key]) body.body[key] = lang;
      });
    }

    // Determine API endpoint based on environment mode
    const mode = (environment as any).apiMode || 'gateway';
    let request$: Observable<TRes>;

    switch (mode) {
      case 'siebel':
        request$ = this.http.post<TRes>(
          `${environment.api_siebel}RunProcess`,
          body,
          { params }
        );
        break;
      case 'soa':
        request$ = this.http.post<TRes>(
          `${environment.api_soa_dev}${apiRoot}/${path}`,
          body,
          { params }
        );
        break;
      default:
        request$ = this.http.post<TRes>(
          this.fullPath(apiRoot, path),
          body,
          { params }
        );
        break;
    }

    // No retry — submit only once
    return request$.pipe(
      catchError((error) => {
        console.error('API request failed:', error);
        return throwError(() => error);
      })
    );
  }

  /** Generic GET */
  // This method is used to get using root, endpoint, and params

  get<TRes = unknown>(
    root: string,
    endpoint: string,
    params?: HttpParams | { [key: string]: string | number }
  ): Observable<TRes> {
    const mode = (environment as any).apiMode || 'gateway';
    let request$: Observable<TRes>;

    if (mode === 'soa') {
      request$ = this.http.get<TRes>(`${environment.api_soa_dev}${root}/${endpoint}`, { params });
    } else {
      // Default/gateway (and siebel does not support GET here)
      request$ = this.http.get<TRes>(this.fullPath(root, endpoint), { params });
    }

    return request$.pipe(
      timeout(60000), // 1 minute
      catchError((error) => {
        console.error('GET request failed:', error);
        return throwError(() => error);
      })
    );
  }

  getBlob(
    root: string,
    endpoint: string,
    params?: HttpParams | { [key: string]: string | number }
  ): Observable<HttpResponse<Blob>> {
    const mode = (environment as any).apiMode || 'gateway';
    const url =
      mode === 'soa'
        ? `${environment.api_soa_dev}${root}/${endpoint}`
        : this.fullPath(root, endpoint);

    return this.http.get(url, {
      params,
      observe: 'response',
      responseType: 'blob',
    }).pipe(
      timeout(60000),
      catchError((error) => {
        console.error('Blob GET request failed:', error);
        return throwError(() => error);
      })
    );
  }

  // This method is used to get available venue room profiles using a dynamic filter query
  getVenueRoomProfiles<TRes = unknown>(
    filter: string,
    scopeKey: string = 'Dubai Chamber Intranet'
  ): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const url = `${gatewayOrigin}/venue/DCVenueBooking/AvailableSlotsREST/AvailableSlots`;
    const startDate = new Date();
    const endDate = new Date(startDate.getTime());
    endDate.setDate(endDate.getDate() + 30);
    const body = {
      scopeKey,
      filter,
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString()
    };

    return this.withVenueToken(() =>
      this.http.post<TRes>(url, body).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Venue room profiles request failed:', error);
          return throwError(() => error);
        })
      )
    );
  }

  // This method is used to get booked room profiles for availability validation
  getBookedRoomProfiles<TRes = unknown>(
    filter: string,
    profileName: string = 'dubai chamber intranet',
    fields: string = 'startTime,endTime',
    pageSize: number = 0,
    restrictFields: string = 'actions'
  ): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const encodedProfileName = encodeURIComponent(profileName);
    const url = `${gatewayOrigin}/venue/DCVenueBooking/GenericGetAPIRest/BookedRoomProfile/${encodedProfileName}`;
    const params = new HttpParams()
      .set('fields', fields)
      .set('filter', filter)
      .set('pageSize', String(pageSize))
      .set('restrictFields', restrictFields);

    return this.withVenueToken(() =>
      this.http.get<TRes>(url, { params }).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Booked room profiles request failed:', error);
          return throwError(() => error);
        })
      )
    );
  }

  // This method is used to load venue booking master data
  getVenueMasterData<TRes = unknown>(payload: Record<string, any>): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const url = `${gatewayOrigin}/venue/DCVenueBooking/VenueBookingREST/getMasterData`;

    return this.withVenueToken(() =>
      this.http.post<TRes>(url, payload).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Venue master data request failed:', error);
          return throwError(() => error);
        })
      )
    );
  }

  getVenueCateringProviderTable(): Observable<string> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const url = `${gatewayOrigin}/venue/DCVenueBooking/CateringProviderTable`;

    return this.withVenueToken(() =>
      this.http.get(url, { responseType: 'text' }).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Venue catering provider table request failed:', error);
          return throwError(() => error);
        })
      )
    );
  }

  // This method is used to reserve venue room
  reserveVenueRoom<TRes = unknown>(
    roomBookingRequest: Record<string, any>,
    attachment?: Blob | File | null,
    attachmentFileName: string = 'Trade_License_Copy.pdf'
  ): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const url = `${gatewayOrigin}/venue/DCVenueBooking/venue-reservation-rest/v1.0/room-book`;
    const formData = new FormData();
    formData.append('roomBookingRequest', JSON.stringify(roomBookingRequest));
    if (attachment) {
      formData.append('attachment', attachment, attachmentFileName);
    }

    return this.withVenueToken(() =>
      this.http.post<TRes>(url, formData).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Reserve room request failed:', error);
          return throwError(() => error);
        })
      )
    );
  }

  // This method is used to get venue booking detail for clarification and cancellation data
  getVenueBookingDetail<TRes = unknown>(
    venueBookingId: string | number,
    restrictFields: string = 'actions'
  ): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const encodedVenueBookingId = encodeURIComponent(String(venueBookingId ?? '').trim());
    const url = `${gatewayOrigin}/venue/DCVenueBooking/GenericGetAPIRest/BookingDetail/${encodedVenueBookingId}`;
    const params = new HttpParams().set('restrictFields', restrictFields);

    return this.withVenueToken(() =>
      this.http.get<TRes>(url, { params }).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Venue booking detail request failed:', error);
          return throwError(() => error);
        })
      )
    );
  }
  // This method is used to update profile using api root, end point, body, and params
  updateProfile<TReq, TRes = unknown>(
    apiRoot: string,
    endPoint: string,
    body: any,
    params?: HttpParams | { [key: string]: string | number }
  ): Observable<TRes> {
    return this.http.patch<TRes>(`${environment.wso2APIURL}${apiRoot}/${endPoint}`, body, { params });
  }
  // This method is used to update venue SR payment status after successful payment
  updateVenueSrPaymentStatus<TRes = unknown>(
    venueBookingId: string | number
  ): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const url = `${gatewayOrigin}/venue/DCVenueBooking/GenericGetAPIRest/PaymentURLStatus`;
    const params = new HttpParams().set('venueBookingId', String(venueBookingId ?? '').trim());

    return this.withVenueToken(() =>
      this.http.get<TRes>(url, { params }).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Venue SR payment status update failed:', error);
          return throwError(() => error);
        })
      )
    );
  }
  // This method is used to process venue booking workflow transitions
  processVenueBookingWorkflow<TRes = unknown>(
    payload: {
      classPK: number;
      transitionName: string;
      comment: string;
    }
  ): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const url = `${gatewayOrigin}/venue/DCVenueBooking/ProcessWorkflowREST/ProcessWorkflow`;

    return this.withVenueToken(() =>
      this.http.post<TRes>(url, payload).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Venue booking workflow process failed:', error);
          return throwError(() => error);
        })
      )
    );
  }

  deleteVenueBooking<TRes = unknown>(
    venueBookingId: string | number
  ): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const encodedVenueBookingId = encodeURIComponent(String(venueBookingId ?? '').trim());
    const url = `${gatewayOrigin}/venue/DCVenueBooking/DeleteBookingREST/DeleteBooking/${encodedVenueBookingId}`;

    return this.withVenueToken(() =>
      this.http.delete<TRes>(url).pipe(
        timeout(60000),
        catchError((error) => {
          console.error('Venue booking delete failed:', error);
          return throwError(() => error);
        })
      )
    );
  }

  createUserInLiferay<TRes = unknown>(
    payload: {
      alternateName: string;
      emailAddress: string;
      familyName: string;
      givenName: string;
      roleExternalReferenceCode: string;
      status: string;
    }
  ): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const url = `${gatewayOrigin}/venue/LiferayToken/CreateUserREST/CreateUser`;

    return this.http.post<TRes>(url, payload).pipe(
      timeout(60000),
      catchError((error) => {
        console.error('Venue liferay user creation failed:', error);
        return throwError(() => error);
      })
    );
  }

  checkUserInLiferay<TRes = unknown>(loginName: string): Observable<TRes> {
    const gatewayOrigin = new URL(environment.api_gateway).origin;
    const normalizedLoginName = String(loginName ?? '').trim();
    const filter = `alternateName eq '${normalizedLoginName}'`;
    const url = `${gatewayOrigin}/venue/LiferayToken/GetUserByLoginNameREST/CheckUserbyLoginName`;
    const params = new HttpParams().set('filter', filter);

    return this.http.get<TRes>(url, { params }).pipe(
      timeout(60000),
      catchError((error) => {
        console.error('Venue liferay user lookup failed:', error);
        return throwError(() => error);
      })
    );
  }
}
